'use client';

import {
  initializePaddle,
  InitializePaddleOptions,
  Paddle,
  PaddleSetupPwCustomer,
} from '@paddle/paddle-js';
import React, { createContext, useContext, useEffect, useState } from 'react';

import usePaddleEvents from '@/features/subscription/hooks/usePaddleEvents';
import {
  paymentService,
  PaymentProduct,
} from '@/features/subscription/services/paymentService';
import useAuth from '@/hooks/useAuth';
import { CheckoutPrewarmProvider } from '@/features/subscription/contexts/CheckoutPrewarm';

type PaddleContextValue = {
  paddleInstance: Paddle | null;
  products: PaymentProduct[] | null;
  isPaddleReady: boolean;
  paddleInitError: Error | null;
};

const PaddleContext = createContext<PaddleContextValue | null>(null);

const resolvePaddleCustomer = (
  user: ReturnType<typeof useAuth>['user']
): PaddleSetupPwCustomer | undefined => {
  if (user?.customerId) {
    return { id: user.customerId };
  }

  if (user?.email) {
    return { email: user.email };
  }

  return undefined;
};

export const PaddleProvider = ({ children }: { children: React.ReactNode }) => {
  const { eventHandler } = usePaddleEvents();
  const { user } = useAuth();
  const [paddleInstance, setPaddleInstance] = useState<Paddle | null>(null);
  const [products, setProducts] = useState<PaymentProduct[] | null>(null);
  const [isPaddleReady, setIsPaddleReady] = useState(false);
  const [paddleInitError, setPaddleInitError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;
    const pwCustomer = resolvePaddleCustomer(user);
    setIsPaddleReady(false);
    setPaddleInitError(null);

    initializePaddle({
      environment: process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT ?? 'sandbox',
      token: process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN!,
      eventCallback: eventHandler,
      checkout: {
        settings: {
          allowLogout: false,
        },
      },
      ...(pwCustomer ? { pwCustomer } : {}),
    } as unknown as InitializePaddleOptions)
      .then((instance) => {
        if (mounted && instance) {
          setPaddleInstance(instance);
          setIsPaddleReady(true);
        }
      })
      .catch((error) => {
        console.error('Error initializing Paddle:', error);
        if (mounted) {
          setPaddleInitError(
            error instanceof Error
              ? error
              : new Error('Failed to initialize Paddle')
          );
        }
      });

    return () => {
      mounted = false;
    };
  }, [eventHandler, user?.customerId, user?.email]);

  useEffect(() => {
    if (!paddleInstance) {
      return;
    }

    const pwCustomer = resolvePaddleCustomer(user);

    if (!pwCustomer) {
      return;
    }

    paddleInstance.Update({ pwCustomer });
  }, [paddleInstance, user?.customerId, user?.email]);

  useEffect(() => {
    let mounted = true;
    if (!user) {
      setProducts(null);
      return () => {
        mounted = false;
      };
    }

    paymentService
      .getProducts()
      .then((response) => {
        const data = response.data as any;
        const list = Array.isArray(data) ? data : data?.data;
        if (mounted) {
          setProducts(Array.isArray(list) ? list : []);
        }
      })
      .catch((error) => {
        console.error('Error fetching Paddle products:', error);
        if (mounted) setProducts([]);
      });

    return () => {
      mounted = false;
    };
  }, [user]);

  return (
    <PaddleContext.Provider
      value={{ paddleInstance, products, isPaddleReady, paddleInitError }}
    >
      <CheckoutPrewarmProvider paddleInstance={paddleInstance}>
        {children}
      </CheckoutPrewarmProvider>
    </PaddleContext.Provider>
  );
};

export const usePaddleContext = () => {
  const ctx = useContext(PaddleContext);
  if (!ctx) {
    throw new Error('usePaddleContext must be used within PaddleProvider');
  }
  return ctx;
};
